/**
 * カード情報のトークン化（PAY.JP のチェックアウト）の下回り。画面に置くボタンは <c>card-checkout.tsx</c>。
 *
 * <b>PAY.JP のチェックアウトをそのまま使う</b>——カード番号はブラウザから
 * PAY.JP へ直接送られ、画面が受け取るのはトークンだけ。画面はそれを <c>CardToken</c> に入れて確定の口へ送る。
 *
 * <b>利用者には PAY.JP が作ったボタンそのものを押してもらう。</b>チェックアウトは、プログラムからの
 * <c>click()</c>（人の操作ではないもの）では入力画面を開かない（2026-10-11 に E2E で確かめた）。
 * そこで、読み込んだときに PAY.JP が作るボタンの入れ物（<c>#payjp_checkout_box</c>）を、画面のボタンを置く場所へ移す。
 * 見た目はポータルのボタンに揃える（<c>styles/forms.css</c>）。
 *
 * <b>CSP は緩めない。</b>今の CSP は読み込み元（script-src）を絞っておらず、
 * <c>https://checkout.pay.jp/</c> を読むだけ。インラインの script は使わない。
 */

/** カードの確認に失敗したとき（PAY.JP の断りの文は出さない）。<b>黙らない</b>——何も出ないと「押しても何も起きない」になる。 */
export const cardFailedMessage =
    "カード情報を確認できませんでした。内容をお確かめのうえ、もう一度お試しください。";

export const cardUnavailableMessage =
    "カード情報の入力画面を開けませんでした。ページを再読み込みしてから、もう一度お試しください。"
    + "解決しない場合は、別のブラウザでお試しください。";

/** PAY.JP の入力画面から返る結果の受け口。 */
export interface CardTokenHandlers {
    readonly created: (token: string) => void;
    readonly failed: () => void;
}

const createdCallback = "onPayjpTokenCreated";
const failedCallback = "onPayjpTokenFailed";

/** 今の画面の受け口（同時に置くボタンは 1 つだけ）。 */
const receiver: { current?: CardTokenHandlers | undefined } = {};

/** 画面を開くたびに、結果の受け口を付け替える。 */
export const listen = (handlers: CardTokenHandlers): void => {
    receiver.current = handlers;
};

/**
 * PAY.JP のスクリプトは、属性に書いた名前のグローバル関数を呼びに来る（外部スクリプトとの取り決め）。
 * <b>読み込む前に置く。</b>PAY.JP は読み込んだときに関数を掴み、後から付け替えたものは呼ばない
 * （2026-10-11 に確かめた。トークンが返っても何も起きなくなる）。中身は今の画面の受け口へ渡すだけにする。
 * <b>型付きの宣言は置かない</b>（外部スクリプトが呼ぶだけのもので、画面のコードからは呼ばない）。
 */
const installCallbacks = (): void => {
    Reflect.set(window, createdCallback, (response: { readonly id: string }) => {
        receiver.current?.created(response.id);
    });
    // PAY.JP の断り（error.message）は英語のことがあり、言い回しもこちらで選べないので出さない
    Reflect.set(window, failedCallback, () => {
        receiver.current?.failed();
    });
};

/**
 * 読み込んだスクリプトを置く、見えない入れ物。PAY.JP はスクリプトの隣にボタンの入れ物を作るので、
 * 画面を離れている間はボタンをここへ戻しておく（画面の部品が消えても、ボタンを捨てない）。
 */
const holder = (): HTMLElement => {
    const existing = document.querySelector<HTMLElement>("[data-member-payjp-holder]");
    if (existing !== null) {
        return existing;
    }
    const created = document.createElement("div");
    created.hidden = true;
    created.dataset["memberPayjpHolder"] = "";
    document.body.append(created);
    return created;
};

/** PAY.JP の決めた属性で読み込む。<b>1 度だけ</b>（読み込むたびにボタンが増える）。 */
const load = (publicKey: string, submitText: string | undefined): Promise<void> => {
    const existing = document.querySelector<HTMLScriptElement>("script[data-member-payjp]");
    if (existing !== null) {
        return Promise.resolve();
    }

    installCallbacks();
    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://checkout.pay.jp/";
        script.className = "payjp-button";
        script.dataset["memberPayjp"] = "";
        script.dataset["key"] = publicKey;
        script.dataset["partial"] = "true";
        script.dataset["text"] = "カード情報を入力";
        if (submitText !== undefined) {
            script.dataset["submitText"] = submitText;
        }
        script.dataset["onCreated"] = createdCallback;
        script.dataset["onFailed"] = failedCallback;
        // 本人認証（3-D セキュア）は別窓で行う。redirect にすると画面ごと移り、持っている入力が失われる
        script.dataset["threeDSecureWorkflow"] = "subwindow";
        script.addEventListener("load", () => { resolve(); }, { once: true });
        script.addEventListener("error", () => { reject(new Error("checkout.pay.jp")); }, { once: true });
        holder().append(script);
    });
};

/** PAY.JP が作るボタンの入れ物（スクリプトの隣の #payjp_checkout_box。中の input[type=button] にクラス名は付かない）。 */
const findBox = async (): Promise<HTMLElement | null> => {
    for (let i = 0; i < 20; i += 1) {
        const box = document.querySelector<HTMLElement>("#payjp_checkout_box");
        if (box?.querySelector("input[type=button]") != null) {
            return box;
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return null;
};

/**
 * PAY.JP のチェックアウトの枠（https://checkout.pay.jp/ の iframe）が読み込み終わるのを待つ。
 * <b>読み込み終わる前にボタンを押すと、「開いて」の合図（postMessage）が空の枠に届いて捨てられ、
 * 入力画面が出ないまま止まる</b>（2026-10-11 に E2E で確かめた）。それまではボタンを見せない。
 * 枠が別のオリジンに移るとこちらから中を読めなくなる（読むと例外になる）ので、それを読み込み終わった印にする。
 */
const waitForCheckoutFrame = async (): Promise<boolean> => {
    for (let i = 0; i < 200; i += 1) {
        const frame = document.querySelector<HTMLIFrameElement>("iframe[src^=\"https://checkout.pay.jp/\"]");
        if (frame !== null) {
            try {
                // 空の枠（about:blank）のうちは同じオリジンなので読める。移った後は例外になる
                if (frame.contentWindow?.location.href === undefined) {
                    return false;
                }
            } catch {
                return true;
            }
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return false;
};

/**
 * 読み込んで、押せる状態のボタンの入れ物を返す。<b>読み込めない・鍵が受け付けられない・枠が開かないときは null</b>
 * （画面は <see cref="cardUnavailableMessage"/> を出し、押せるボタンを置かない）。
 */
export const prepareCheckout = async (
    publicKey: string, submitText: string | undefined,
): Promise<HTMLElement | null> => {
    try {
        await load(publicKey, submitText);
    } catch {
        return null;
    }
    const box = await findBox();
    if (box === null || !await waitForCheckoutFrame()) {
        return null;
    }
    return box;
};

/** 画面を離れるとき、ボタンの入れ物を見えない入れ物へ戻す。 */
export const releaseCheckout = (box: HTMLElement): void => {
    receiver.current = undefined;
    holder().append(box);
};
